import React, { useState, useEffect, useCallback } from 'react';
import { clinicService } from '../../services/api';

const Step1_ClinicSelection = ({
  selectedClinic,
  onClinicSelect,
  onClinicCreate,
  onNext,
  loading,
  error,
  setError
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    id: '',
    name: '',
    region: 'HK'
  });
  const [searchLoading, setSearchLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  // Search for clinics
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    setError('');

    try {
      console.log('Searching for clinics with query:', searchQuery);
      const response = await clinicService.searchClinicConfigurations({
        name: searchQuery,
        limit: 10
      });

      if (response.success && response.data) {
        console.log('Search results:', response.data);
        setSearchResults(response.data);
      } else {
        setError(response.message || 'Failed to search clinics');
        setSearchResults([]);
      }
    } catch (err) {
      console.error('Error searching clinics:', err);
      setError(err.response?.data?.detail || err.message || 'An error occurred while searching clinics');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [searchQuery]);

  // Handle clinic selection
  const handleClinicSelect = (clinic) => {
    console.log('Selected clinic:', clinic);
    onClinicSelect(clinic);
    setSearchResults([]);
    setSearchQuery('');
  };

    // Handle create form submission
  const handleCreateSubmit = async (e) => {
    e.preventDefault();

    if (!createFormData.id.trim()) {
      setError('Please enter a clinic ID');
      return;
    }

    if (!createFormData.name.trim()) {
      setError('Please enter a clinic name');
      return;
    }

    setCreateLoading(true);
    setError('');

    try {
      console.log('Creating new clinic:', createFormData);
      const response = await onClinicCreate(createFormData);

      if (response && response.success) {
        console.log('Clinic created successfully:', response);
        // Select the newly created clinic
        if (response.data) {
          onClinicSelect(response.data);
        }
        setShowCreateForm(false);
        setCreateFormData({ id: '', name: '', region: 'HK' });
      }
    } catch (err) {
      console.error('Error creating clinic:', err);
      setError(err.response?.data?.detail || err.message || 'An error occurred while creating clinic');
    } finally {
      setCreateLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setCreateFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle search input change with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        handleSearch();
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 1: Select or Create a Clinic</h2>
        <p className="text-gray-600">Choose an existing clinic or create a new one to get started</p>
      </div>

      {/* Search Section */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Search Existing Clinics</h3>

        <div className="flex gap-3 mb-4">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Search by clinic name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchLoading && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            )}
          </div>
          <button
            onClick={handleSearch}
            disabled={searchLoading || !searchQuery.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {searchLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Searching...
              </>
            ) : (
              'Search'
            )}
          </button>
        </div>

        {/* Search Results */}
        {searchLoading && searchQuery.trim() && (
          <div className="text-center py-8">
            <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-600">Searching for clinics...</p>
          </div>
        )}

        {!searchLoading && searchResults.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-gray-700">Search Results:</h4>
            {searchResults.map((clinic) => (
              <div
                key={clinic.id}
                className={`p-3 border rounded-md cursor-pointer transition-colors ${
                  selectedClinic?.id === clinic.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
                onClick={() => handleClinicSelect(clinic)}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-medium text-gray-900">{clinic.name}</div>
                    <div className="text-sm text-gray-600">ID: {clinic.id}</div>
                    {clinic.region && (
                      <div className="text-sm text-gray-600">Region: {clinic.region}</div>
                    )}
                  </div>
                  {selectedClinic?.id === clinic.id && (
                    <div className="text-blue-600">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!searchLoading && searchQuery.trim() && searchResults.length === 0 && (
          <div className="text-gray-500 text-center py-4">
            No clinics found. Try creating a new one instead.
          </div>
        )}
      </div>

      {/* Create New Clinic Section */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Create New Clinic</h3>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            {showCreateForm ? 'Cancel' : 'Create New'}
          </button>
        </div>

        {showCreateForm && (
          <form onSubmit={handleCreateSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Clinic ID (CLID) *
              </label>
              <input
                type="text"
                value={createFormData.id}
                onChange={(e) => handleInputChange('id', e.target.value)}
                placeholder="Enter clinic ID (e.g., clinic_name)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">This will be used as the unique identifier for the clinic</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Clinic Name *
              </label>
              <input
                type="text"
                value={createFormData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter clinic name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Region
              </label>
              <select
                value={createFormData.region}
                onChange={(e) => handleInputChange('region', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="HK">Hong Kong</option>
                <option value="SG">Singapore</option>
              </select>
            </div>



            <button
              type="submit"
              disabled={createLoading || !createFormData.name.trim()}
              className="w-full px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createLoading ? 'Creating...' : 'Create Clinic'}
            </button>
          </form>
        )}
      </div>

      {/* Selected Clinic Display */}
      {selectedClinic && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Selected Clinic</h3>
          <div className="space-y-2">
            <div><span className="font-medium">Name:</span> {selectedClinic.name}</div>
            <div><span className="font-medium">ID:</span> {selectedClinic.id}</div>
            {selectedClinic.region && (
              <div><span className="font-medium">Region:</span> {selectedClinic.region}</div>
            )}
            {selectedClinic.contact_number && (
              <div><span className="font-medium">Contact:</span> {selectedClinic.contact_number}</div>
            )}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      {/* Next Button */}
      <div className="flex justify-end">
        <button
          onClick={onNext}
          disabled={!selectedClinic || loading}
          className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {loading ? 'Loading...' : 'Next Step'}
        </button>
      </div>
    </div>
  );
};

export default Step1_ClinicSelection;
